import { BrowserRouter, Route, Routes } from "react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { ToastProvider } from "@nthamizh/ui";
import { ApiClientProvider } from "@platform/hooks";
import { apiClient } from "./lib/apiClient";
import { queryClient } from "./lib/queryClient";

import { ProtectedRoute } from "./routes/ProtectedRoute";
import { AdminRoute, EnterpriseAdminRoute, ConfigIQRoute, CostSimRoute, SupportRoute } from "./routes/AdminRoute";
import { RootRoute } from "./routes/RootRoute";

import { LoginPage } from "./pages/LoginPage";
import { HandoffPage } from "./pages/auth/HandoffPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";

import { EnterpriseAppShell } from "./components/enterprise/AppShell";

// Enterprise pages
import { EnterpriseDashboardPage } from "./pages/enterprise/DashboardPage";
import { EnterpriseUsersPage } from "./pages/enterprise/UsersPage";
import { EnterpriseRolesPage } from "./pages/enterprise/RolesPage";
import { EnterpriseAppAccessPage } from "./pages/enterprise/AppAccessPage";
import { EnterpriseSettingsPage } from "./pages/enterprise/SettingsPage";
import { InviteUserPage } from "./pages/enterprise/InviteUserPage";

// Profile pages
import { ProfileLayout, PasswordPage, SessionsPage, MyFeedbackPage } from "./pages/profile/ProfilePage";
import { MyFilesPage } from "./pages/MyFilesPage";

// Platform admin pages
import { PlatformDashboardPage } from "./pages/platform/DashboardPage";
import { PlatformEnterprisesPage } from "./pages/platform/EnterprisesPage";
import { PlatformAllUsersPage } from "./pages/platform/AllUsersPage";
import { PlatformSystemHealthPage } from "./pages/platform/SystemHealthPage";
import { PlatformRoleBuilderPage } from "./pages/platform/RoleBuilderPage";
import { PlatformEmailSettingsPage } from "./pages/platform/EmailSettingsPage";
import { PlatformSchedulerPage } from "./pages/platform/SchedulerPage";
import { PlatformSchedulerHistoryPage } from "./pages/platform/SchedulerHistoryPage";
import { PlatformEnterpriseDetailPage } from "./pages/platform/EnterpriseDetailPage";
import { PlatformEnterpriseUsersPage } from "./pages/platform/enterprise/EnterpriseUsersPage";
import { PlatformEnterpriseRolesPage } from "./pages/platform/enterprise/EnterpriseRolesPage";
import { PlatformEnterpriseAccessPage } from "./pages/platform/enterprise/EnterpriseAccessPage";
import { PlatformEnterpriseSettingsPage } from "./pages/platform/enterprise/EnterpriseSettingsPage";
import { EnterpriseUserDetailPage } from "./pages/platform/enterprise/EnterpriseUserDetailPage";
import { ConfigIQLoader } from "./pages/configiq/ConfigIQLoader";
import { CostSimLoader } from "./pages/costsim/CostSimLoader";
import { EnterpriseUserDetailPage as EnterpriseAdminUserDetailPage } from "./pages/enterprise/UserDetailPage";
import { PlatformAuditLogPage } from "./pages/platform/AuditLogPage";
import { PlatformUserDetailPage } from "./pages/platform/UserDetailPage";
import { PlatformFeedbackReviewPage } from "./pages/platform/FeedbackReviewPage";

// Legacy access page
import { AccessPage } from "./pages/AccessPage";

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ApiClientProvider client={apiClient}>
        <ToastProvider>
          <BrowserRouter>
            <Routes>
              {/* Public */}
              <Route path="/" element={<RootRoute />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/auth/handoff" element={<HandoffPage />} />

              {/* Authenticated — enterprise shell */}
              <Route element={<ProtectedRoute />}>
                <Route element={<EnterpriseAppShell />}>
                  {/* Dashboard */}
                  <Route path="/dashboard" element={<EnterpriseDashboardPage />} />
                  <Route path="/home" element={<EnterpriseDashboardPage />} />

                  {/* Legacy */}
                  <Route path="/access" element={<AccessPage />} />

                  {/* Profile */}
                  <Route path="/profile" element={<ProfileLayout />}>
                    <Route index element={<ProfileLayout />} />
                    <Route path="password" element={<PasswordPage />} />
                    <Route path="sessions" element={<SessionsPage />} />
                    <Route path="feedback" element={<MyFeedbackPage />} />
                  </Route>

                  {/* Files — any authenticated user, own files only. Not
                      nested under /profile: unlike feedback (occasional,
                      naturally profile-adjacent), this is a primary,
                      frequently-used tool and gets its own top-level
                      sidebar entry rather than living as a profile
                      sub-page. */}
                  <Route path="/my-files" element={<MyFilesPage />} />

                  {/* Enterprise admin */}
                  <Route element={<EnterpriseAdminRoute />}>
                    <Route path="/enterprise/users" element={<EnterpriseUsersPage />} />
                    <Route path="/enterprise/users/invite" element={<InviteUserPage />} />
                    <Route path="/enterprise/users/:userId" element={<EnterpriseAdminUserDetailPage />} />
                    <Route path="/enterprise/roles" element={<EnterpriseRolesPage />} />
                    <Route path="/enterprise/apps" element={<EnterpriseAppAccessPage />} />
                    <Route path="/enterprise/settings" element={<EnterpriseSettingsPage />} />
                  </Route>

                  {/* Scheduler — platform or enterprise admin, same guard as
                      the block above (EnterpriseAdminRoute already does
                      exactly "platform admin bypasses this, or has
                      enterprise-admin-level access", matching the backend's
                      own requireEnterpriseAdmin exactly) — a separate block
                      since /platform/scheduler isn't an /enterprise/* path,
                      even though it shares the same access rule. */}
                  <Route element={<EnterpriseAdminRoute />}>
                    <Route path="/platform/scheduler" element={<PlatformSchedulerPage />} />
                    <Route path="/platform/scheduler/history" element={<PlatformSchedulerHistoryPage />} />
                  </Route>

                  {/* ConfigIQ — its own permission model, reachable regardless of
                      subdomain: platform admins, enterprise admins, and any user
                      (individual or enterprise-scoped) holding a design-module
                      role. See ConfigIQRoute for why this is not folded into
                      EnterpriseAdminRoute above.
                      A single splat route on purpose: the remote renders its own
                      descendant <Routes>, which react-router only allows to
                      match deeper paths when the parent ends in "*". A separate
                      exact "/configiq" route would win the match at exactly
                      /configiq and hand the remote a splat-less parent,
                      triggering the "descendant <Routes> ... no trailing *"
                      warning. The splat alone matches /configiq too (* = ""). */}
                  <Route element={<ConfigIQRoute />}>
                    <Route path="/configiq/*" element={<ConfigIQLoader />} />
                  </Route>

                  {/* CostSimulator — separate route guard from ConfigIQ:
                      cost_simulator module roles are checked independently
                      so the two products can be licensed/enabled per
                      enterprise without one implying access to the other.
                      Same splat reasoning as /configiq/* above. */}
                  <Route element={<CostSimRoute />}>
                    <Route path="/costsim/*" element={<CostSimLoader />} />
                  </Route>

                  {/* Platform admin */}
                  <Route element={<AdminRoute />}>
                    <Route path="/platform" element={<PlatformDashboardPage />} />
                    <Route path="/platform/enterprises" element={<PlatformEnterprisesPage />} />
                    <Route path="/platform/enterprises/:id" element={<PlatformEnterpriseDetailPage />} />
                    <Route path="/platform/enterprises/:id/users" element={<PlatformEnterpriseUsersPage />} />
                    <Route path="/platform/enterprises/:id/users/:userId" element={<EnterpriseUserDetailPage />} />
                    <Route path="/platform/enterprises/:id/roles" element={<PlatformEnterpriseRolesPage />} />
                    <Route path="/platform/enterprises/:id/access" element={<PlatformEnterpriseAccessPage />} />
                    <Route path="/platform/enterprises/:id/settings" element={<PlatformEnterpriseSettingsPage />} />
                    <Route path="/platform/users" element={<PlatformAllUsersPage />} />
                    <Route path="/platform/users/:id" element={<PlatformUserDetailPage />} />
                    <Route path="/platform/health" element={<PlatformSystemHealthPage />} />
                    <Route path="/platform/roles" element={<PlatformRoleBuilderPage />} />
                    <Route path="/platform/audit" element={<PlatformAuditLogPage />} />
                    <Route path="/platform/email-settings" element={<PlatformEmailSettingsPage />} />
                  </Route>

                  {/* Feedback review — support and platform admin have equal,
                      full access (not admin-exclusive like the block above),
                      so this is its own guard rather than folded into
                      AdminRoute. Support currently can't reach anything else
                      under /platform/* (AdminRoute above is platform_admin
                      only, despite AppShell's sidebar showing the whole
                      Platform section to support too — a pre-existing gap
                      not touched here), so this route is genuinely the
                      first thing under /platform/* support can actually
                      open. */}
                  <Route element={<SupportRoute />}>
                    <Route path="/platform/feedback" element={<PlatformFeedbackReviewPage />} />
                  </Route>
                </Route>
              </Route>
            </Routes>
          </BrowserRouter>
        </ToastProvider>
      </ApiClientProvider>
    </QueryClientProvider>
  );
}
